import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';

import {
  Box,
  Button,
  TextField,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  FormLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Typography,
  Divider,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

import ComboBox, { DefaultDataType as ComboboxDataType } from '../layout/ComboBox';
import { fetchTrialTags } from '../../redux/actions';
import {
  RootState,
  Trial,
  TrialCategoryValue,
  TrialTagMapping,
} from '../../redux/types';

const certHashTable: { [key: string]: { text: string, disable: boolean } } = {
  DefaultSuccess: { text: 'Pathologie Erfolg (DE)', disable: false },
  DefaultParticipation: { text: 'Pathologie Teilnahmebescheinigung (DE)', disable: false },
  DefaultSuccessInternational: { text: 'Pathology Success (EN)', disable: false },
  DefaultSuccessESPInternational: { text: 'ESP Pathology Success (EN)', disable: false },
  DefaultParticipationESPInternational: { text: 'ESP Pathology Participation (EN)', disable: false },
  DefaultSuccessMultimarkerESPInternational: { text: 'ESP Multimaker Pathology Success (EN)', disable: false },
  DefaultParticipationMultimarkerESPInternational: { text: 'ESP Multimarker Patholog Participation (EN)', disable: false },
  DefaultSuccessMultimarkerESPInternationalNoReport: { text: 'ESP Multimaker Pathology Success (EN) -- no report', disable: false },
  DefaultParticipationMultimarkerESPInternationalNoReport: { text: 'ESP Multimarker Patholog Participation (EN) -- no report', disable: false },
  DefaultParticipationInternational: { text: 'Pathology Participant (EN)', disable: false },
  NeuropathologySuccess: { text: 'Neuropathologie Erfolg (DE)', disable: false },
  NeuropathologyParticipation: { text: 'Neuropathologie Teilnahmebescheinigung (DE)', disable: false },
  NeuropathologySuccessInternational: { text: 'Neuropathology Success (EN)', disable: false },
  NeuropathologyParticipationInternational: { text: 'Neuropathology Participant (EN)', disable: false },
  DefaultMulti: { text: 'Multi-Seiten Zertifikate (mit Teilnahmebescheinigung) (DE)', disable: false },
  DefaultMultiInternational: { text: 'Multi-page Certificates (with Participant) (EN)', disable: false },
  DefaultMultiESPInternational: { text: 'ESP Multi-page Certificates (with Participant) (EN)', disable: false },
  DefaultMultiMarkerSuccess: { text: 'Multimarker Zertifikate Erfolg (DE)', disable: false },
  DefaultParticipationMultimarker: { text: 'Multimarker Zertifikate Teilnahmebescheinigung (DE)', disable: false },
  DefaultMultiMarkerDRTSuccess: { text: 'Multimarker + DRT Erfolg (DE)', disable: false },
  // Not to be confused with the DRT green template.
  DefaultParticipationMultimarkerDRT: { text: 'Multimarker + DRT Teilnahme (DE)', disable: false },
  DefaultMultiMarkerSuccessInternational: { text: 'Multimarker Certificate Success (EN)', disable: false },
  DefaultParticipationMultimarkerInternational: { text: 'Multimarker Certificate Participant (EN)', disable: false },
  Dummy: { text: 'Dummy (DE, EN)', disable: false },
  DefaultSuccessDRT: { text: 'DRT Success Certificate (EN)', disable: true },
  Custom: { text: 'Custom Template (contact developer)', disable: true },
};

const reviewCategory: { [key: number]: { text: React.ReactNode, disable: boolean } } = {
  1: { text: 'Normal', disable: false },
  2: { text: <FormattedMessage id="trialSetup.educativeComments" />, disable: true },
  3: { text: <FormattedMessage id="trialSetup.textblockComments" />, disable: false },
};

function trialAvailabilityMapper(trial: Trial): TrialAvailabilityKeys {
  if (!trial.international && !trial.availableBoth) {
    return 'regional';
  } if (trial.international && !trial.availableBoth) {
    return 'international';
  } return 'availableBoth';
}

type ExtendedTrial = Trial & {
  trialTagMapping?: TrialTagMapping[];
}
type Props = PropsFromRedux & {
  trial?: ExtendedTrial,
  onSave: (trial: ExtendedTrial, selectedTags: ComboboxDataType[] | null) => void;
  onCancel: () => void;
  onDuplicate?: (trial: Trial) => void;
}

type TrialAvailabilityKeys = 'regional' | 'international' | 'availableBoth';

const TrialMetadataDialog: React.FC<Props> = ({
  trial: propTrial = {
    trialCategory: TrialCategoryValue.normal,
    reviewCategoryId: 1,
    version: 2,
  } as ExtendedTrial,
  onSave,
  onCancel,
  onDuplicate,
  trialCategories,
  fetchTrialTags: fetchTrialTagAction,
  trialTags,
}: Props) => {
  const [trial, setTrial] = useState<ExtendedTrial>(propTrial);
  const [tagOptions, setTagOptions] = useState<ComboboxDataType[]>([]);
  const [trialTagMapping] = useState(propTrial.trialTagMapping || []);
  const [selectedTags, setSelectedTags] = useState<ComboboxDataType[] | null>([]);
  const [
    disableDRTUnrelatedFields,
    setDisableDRTUnrelatedFields,
  ] = useState<boolean>(propTrial.isDRT);
  const [trialAvailability, setTrialAvailability] = useState<TrialAvailabilityKeys>(
    trialAvailabilityMapper(propTrial),
  );
  const [disableAdditionalPrice, setDisableAdditionalPrice] = useState<boolean>(false);

  const TEXT_VARIANT = 'outlined';

  useEffect(() => {
    if (!trialTags) {
      fetchTrialTagAction();
    } else {
      setTagOptions(Object.keys(trialTags).map((key) => ({
        value: trialTags[Number(key)].tagId,
        label: trialTags[Number(key)].name,
      })));
    }
  }, [fetchTrialTagAction, trialTags]);

  useEffect(() => {
    if (trialTagMapping.length === 0 || !trialTags) {
      return;
    }
    setSelectedTags(trialTagMapping.map((tag) => ({
      value: trialTags[tag.tagId].tagId,
      label: trialTags[tag.tagId].name,
    })));
  }, [trialTagMapping, trialTags]);

  const onChangeValue = (key: keyof Trial, value: any) => {
    const updatedObject = { [key]: value };
    if (key.toLocaleLowerCase().includes('date')) {
      updatedObject[key] = value.format('YYYY-MM-DDTHH:mm:ss');
    }
    if (key === 'priceInEUR' && value <= 0) {
      setDisableAdditionalPrice(true);
      updatedObject.discardAdminFee = false;
      updatedObject.discardShippingFee = false;
    } else {
      setDisableAdditionalPrice(false);
    }
    setTrial({ ...trial, ...updatedObject });
  };

  const onIsDRTChange = (value: boolean) => {
    const trialObject: Partial<Trial> = {};
    if (value) {
      if (!trialObject.certificateTemplates) {
        trialObject.certificateTemplates = {};
      }
      trialObject.certificateTemplates.success = 'DefaultSuccessDRT';
      trialObject.certificateTemplates.fail = '';
      trialObject.participants = 9999;
      trialObject.samples = '9999';
    } else if (trialAvailability === 'international' || trialAvailability === 'availableBoth') {
      trialObject.certificateTemplates = {
        success: 'DefaultSuccessInternational',
        fail: 'DefaultParticipationInternational',
      };
    } else {
      trialObject.certificateTemplates = {
        success: 'DefaultSuccess',
        fail: 'DefaultParticipation',
      };
    }
    setDisableDRTUnrelatedFields(value);
    setTrial({ ...trial, ...trialObject, isDRT: value });
  };

  const onChangeTrialAvailability = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = (event.target as HTMLInputElement).value as TrialAvailabilityKeys;
    setTrialAvailability(selectedOption);
    const trialObject: Partial<Trial> = {};
    if (selectedOption === 'regional') {
      trialObject.availableBoth = false;
      trialObject.international = false;
    } else if (selectedOption === 'international') {
      trialObject.international = true;
      trialObject.availableBoth = false;
    } else if (selectedOption === 'availableBoth') {
      trialObject.international = true;
      trialObject.availableBoth = true;
    }
    setTrial({ ...trial, ...trialObject });
  };

  const onChangeTrialRegistrationMode = (category: number) => {
    const trialObject: Partial<Trial> = {
      trialCategory: category,
    };

    setTrial({ ...trial, ...trialObject });
  };

  type certificateSelected = {
    for: string,
    value: string,
  };
  const onChangeCertificateTemplates = (key: string, value: certificateSelected) => {
    const updatedObject = {
      certificateTemplates: { ...trial.certificateTemplates },
    };
    updatedObject.certificateTemplates = {
      ...updatedObject.certificateTemplates,
      [value.for]: value.value,
    };
    setTrial({ ...trial, ...updatedObject });
  };

  const changeTagHandler = (value: ComboboxDataType[] | null) => {
    setSelectedTags(value);
  };

  const disableFunction = propTrial ? propTrial.deactivate : false;

  const nameOnCertificate = () => {
    if (trial && trial.nameOnCertificate) {
      return trial.nameOnCertificate;
    }
    return '';
  };

  const renderTrialSettings = () => (
    <Paper variant="outlined" square>
      <div className="pa3">
        <Typography variant="h6" className="pb2"><FormattedMessage id="trialSetup.trialsettingHeadline" /></Typography>
        <div className="pb3">
          <TextField
            label={<FormattedMessage id="trialSetup.trialName" />}
            onChange={(e) => onChangeValue('name', e.target.value)}
            value={trial?.name}
            style={{ width: '100%' }}
            disabled={disableFunction}
            variant={TEXT_VARIANT}
            size="small"
          />
        </div>
        <div className="pb3">
          <TextField
            variant={TEXT_VARIANT}
            size="small"
            label={<FormattedMessage id="trialSetup.trialCode" />}
            onChange={(e) => onChangeValue('code', e.target.value)}
            value={trial?.code}
            style={{ width: '100%' }}
            disabled={disableFunction}
          />
        </div>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box display="flex" gap={1} mb={1}>
            <DatePicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(props) => <TextField fullWidth {...props} label={<FormattedMessage id="trialSetup.registrationStartDate" />} />}
              value={trial.registrationStartDate || null}
              views={['year', 'month', 'day']}
              onChange={(date: Moment | null) => onChangeValue('registrationStartDate', date)}
              disabled={disableFunction}
              inputFormat="DD/MM/yyyy"
              mask="__.__.____"
              maxDate={trial.registrationEndDate ? moment(trial.registrationEndDate) : undefined}
            />
            <DatePicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(props) => <TextField fullWidth {...props} label={<FormattedMessage id="trialSetup.registrationEndDate" />} />}
              value={trial.registrationEndDate || null}
              views={['year', 'month', 'day']}
              onChange={(date: Moment | null) => onChangeValue('registrationEndDate', date)}
              disabled={disableFunction}
              inputFormat="DD/MM/yyyy"
              mask="__.__.____"
              minDate={trial.registrationStartDate
                ? moment(trial.registrationStartDate) : undefined}
            />
          </Box>
          <Box display="flex" gap={1} mb={1}>
            <DatePicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(props) => <TextField fullWidth {...props} label={<FormattedMessage id="trialSetup.startDate" />} />}
              views={['year', 'month', 'day']}
              value={trial.startDate || null}
              onChange={(date: Moment | null) => onChangeValue('startDate', date)}
              disabled={disableFunction}
              inputFormat="DD/MM/yyyy"
              mask="__.__.____"
              maxDate={trial.endDate ? moment(trial.endDate) : undefined}
            />
            <DatePicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(props) => <TextField fullWidth {...props} label={<FormattedMessage id="trialSetup.endDate" />} />}
              views={['year', 'month', 'day']}
              value={trial.endDate || null}
              onChange={(date: Moment | null) => onChangeValue('endDate', date)}
              disabled={disableFunction}
              inputFormat="DD/MM/yyyy"
              mask="__.__.____"
              minDate={trial.startDate ? moment(trial.startDate) : undefined}
            />
          </Box>
        </LocalizationProvider>
        <div className="flex pb3">
          <div className="mr3" style={{ flexBasis: '50%' }}>
            <FormControl component="fieldset">
              <FormLabel component="legend"><FormattedMessage id="trialSetup.trialAvailability" /></FormLabel>
              <RadioGroup aria-label="trialAvalibility" name="trialAvailability" value={trialAvailability} onChange={onChangeTrialAvailability}>
                <Box><FormControlLabel value="regional" control={<Radio />} label="Regional" /></Box>
                <Box><FormControlLabel value="international" control={<Radio />} label={<FormattedMessage id="trialSetup.trialIsInternationl" />} /></Box>
                <Box><FormControlLabel value="availableBoth" control={<Radio />} label={<FormattedMessage id="trialSetup.availableForBoth" />} /></Box>
              </RadioGroup>
            </FormControl>
          </div>
          <div style={{ flexBasis: '50%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <FormControl component="fieldset">
                <FormLabel component="legend"><FormattedMessage id="trialSetup.isDRT" /></FormLabel>
                <Box>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        name="international"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          onIsDRTChange(event.target.checked);
                        }}
                        checked={trial?.isDRT || false}
                      />
                    )}
                    label={<FormattedMessage id="trialSetup.makeAsDRT" />}
                  />
                </Box>
              </FormControl>
              <FormControl component="fieldset">
                <FormLabel component="legend"><FormattedMessage id="trialSetup.accreditedMessage" /></FormLabel>
                <Box>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        name="isAccredited"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          setTrial({ ...trial, isAccredited: event.target.checked });
                        }}
                        checked={trial?.isAccredited || false}
                      />
                    )}
                    label={<FormattedMessage id="trialSetup.accreditedMessage2" />}
                  />
                </Box>
              </FormControl>
            </Box>
          </div>
        </div>
        <Divider />
        <div className="pt3">
          <FormControl fullWidth>
            <div className="pb2">
              <FormLabel component="legend">Trial Tags</FormLabel>
            </div>
            <Typography>
              <ComboBox
                keyId="trialTags"
                data={tagOptions}
                selectedItems={selectedTags}
                onChange={changeTagHandler}
                isMulti
              />
            </Typography>
          </FormControl>
        </div>
      </div>
    </Paper>
  );

  const renderRegistrationSettings = () => (
    <Paper variant="outlined" square>
      <div className="pa3">
        <Typography variant="h6" className="pb2"><FormattedMessage id="trialSetup.registrationHeadline" /></Typography>
        <div className="pb3">
          <FormControl style={{ width: '100%' }} variant="outlined">
            <InputLabel><FormattedMessage id="trialSetup.trialCategory" /></InputLabel>
            <Select
              label={<FormattedMessage id="trialSetup.trialCategory" />}
              value={trial.trialCategory || 1}
              style={{ width: '100%' }}
              onChange={(event) => {
                onChangeTrialRegistrationMode(event.target.value as number);
              }}
              disabled={disableFunction}
            >
              {
                trialCategories.map((category) => (
                  <MenuItem
                    key={category.id}
                    value={category.id}
                  >
                    {category.name}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </div>
        <div className="flex pb3">
          <div className="mr2" style={{ flexBasis: '50%' }}>
            <TextField
              variant="outlined"
              size="small"
              label={<FormattedMessage id="trialSetup.amountParticipants" />}
              type="number"
              onChange={(e) => onChangeValue('participants', Number(e.target.value))}
              value={trial.participants || 0}
              style={{ width: '100%' }}
              disabled={disableFunction || disableDRTUnrelatedFields}
            />
          </div>
          <div className="ml2" style={{ flexBasis: '50%' }}>
            <TextField
              variant="outlined"
              size="small"
              label={<FormattedMessage id="trialSetup.numberOfSamples" />}
              type="string"
              onChange={(e) => onChangeValue('samples', e.target.value.toString())}
              value={trial.samples || 0}
              style={{ width: '100%' }}
              disabled={disableFunction || disableDRTUnrelatedFields}
            />
          </div>
        </div>
        <div className="flex pb3">
          <div className="mr2" style={{ flexBasis: '50%' }}>
            <TextField
              variant="outlined"
              size="small"
              label={<FormattedMessage id="trialSetup.costCenter" />}
              onChange={(e) => onChangeValue('costCenter', e.target.value.trim())}
              value={trial && trial.costCenter}
              style={{ width: '100%' }}
              disabled={disableFunction}
            />
          </div>
          <div className="ml2" style={{ flexBasis: '50%' }}>
            <TextField
              variant="outlined"
              size="small"
              label={<FormattedMessage id="trialSetup.price" />}
              type="number"
              onChange={(e) => onChangeValue('priceInEUR', Number(e.target.value))}
              value={trial && trial.priceInEUR}
              style={{ width: '100%' }}
              disabled={disableFunction}
            />
          </div>
        </div>
        <div className="flex">
          <div style={{ flexBasis: '50%' }}>
            <FormControl component="fieldset" disabled={disableAdditionalPrice}>
              <FormLabel component="legend"><FormattedMessage id="trialSetup.excludeAdminFee" /></FormLabel>
              <FormControlLabel
                control={(
                  <div className="pv2">
                    <Checkbox
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onChangeValue('discardAdminFee', event.target.checked);
                      }}
                      checked={trial?.discardAdminFee || false}
                    />
                  </div>
                )}
                label={<FormattedMessage id="trialSetup.excludeAdminFeeText" />}
              />
            </FormControl>
          </div>
          <div style={{ flexBasis: '50%' }}>
            <FormControl component="fieldset" disabled={disableAdditionalPrice}>
              <FormLabel component="legend"><FormattedMessage id="trialSetup.excludeShippingFee" /></FormLabel>
              <FormControlLabel
                control={(
                  <div className="pv2">
                    <Checkbox
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onChangeValue('discardShippingFee', event.target.checked);
                      }}
                      checked={trial?.discardShippingFee || false}
                    />
                  </div>
                )}
                label={<FormattedMessage id="trialSetup.excludeShippingFeeText" />}
              />
            </FormControl>
          </div>
        </div>
      </div>
    </Paper>
  );

  const renderCertificateSettings = () => {
    let successTemplate = trial.certificateTemplates?.success;
    let participantTemplate = trial.certificateTemplates?.fail;

    if (successTemplate
      && !Object.prototype.hasOwnProperty.call(certHashTable, successTemplate)) {
      successTemplate = 'Custom';
    }

    if (participantTemplate
      && !Object.prototype.hasOwnProperty.call(certHashTable, participantTemplate)) {
      participantTemplate = 'Custom';
    }

    const disableCertificateSelection = disableFunction || successTemplate === 'Custom' || disableDRTUnrelatedFields;
    return (
      <Paper variant="outlined" square>
        <div className="pa3">
          <Typography variant="h6" className="pb2"><FormattedMessage id="trialSetup.certificateHeadline" /></Typography>
          <div className="flex pb3">
            <div className="mr2" style={{ flexBasis: '50%' }}>
              <FormControl style={{ width: '100%' }} variant="outlined">
                <InputLabel><FormattedMessage id="trialSetup.successTemplate" /></InputLabel>
                <Select
                  label="Success Certificate Template"
                  value={successTemplate || ''}
                  style={{ width: '100%' }}
                  onChange={(event) => onChangeCertificateTemplates('certificateTemplates', {
                    for: event.target.name as string,
                    value: event.target.value as string,
                  })}
                  disabled={disableCertificateSelection}
                  name="success"
                >
                  <MenuItem value="">Keines</MenuItem>
                  {
                    Object.entries(certHashTable).map(([key, value]) => (
                      <MenuItem
                        key={key}
                        value={key}
                        disabled={disableCertificateSelection || value.disable}
                      >
                        {value.text}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </div>
            <div className="ml2" style={{ flexBasis: '50%' }}>
              <FormControl style={{ width: '100%' }} variant="outlined">
                <InputLabel><FormattedMessage id="trialSetup.failTemplate" /></InputLabel>
                <Select
                  label="Fail Certificate Template"
                  value={participantTemplate || ''}
                  style={{ width: '100%' }}
                  onChange={(event) => onChangeCertificateTemplates('certificateTemplates', {
                    for: event.target.name as string,
                    value: event.target.value as string,
                  })}
                  disabled={disableCertificateSelection}
                  name="fail"
                >
                  <MenuItem value="">None</MenuItem>
                  {
                    Object.entries(certHashTable).map(([key, value]) => (
                      <MenuItem
                        key={key}
                        value={key}
                        disabled={disableCertificateSelection || value.disable}
                      >
                        {value.text}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
            </div>
          </div>
          <div className="pb3">
            <TextField
              label={<FormattedMessage id="trialSetup.leadOnCertificate" />}
              onChange={(e) => onChangeValue('leadOnCertificate', e.target.value)}
              value={
                trial
                && trial.leadOnCertificate
                && trial.leadOnCertificate
              }
              style={{ width: '100%' }}
              disabled={disableFunction}
              variant={TEXT_VARIANT}
              multiline
              maxRows={4}
              size="small"
            />
          </div>
          <div className="pb3">
            <TextField
              label={<FormattedMessage id="trialSetup.nameOnCertificate" />}
              onChange={(e) => onChangeValue('nameOnCertificate', e.target.value)}
              value={nameOnCertificate()}
              style={{ width: '100%' }}
              disabled={disableFunction}
              variant={TEXT_VARIANT}
              size="small"
            />
          </div>
          <div style={{ flexBasis: '50%' }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Upload Certificate Manually</FormLabel>
              <FormControlLabel
                control={(
                  <div className="pv2">
                    <Checkbox
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onChangeValue('manualUploadCert', event.target.checked);
                      }}
                      checked={trial?.manualUploadCert || false}
                    />
                  </div>
                )}
                label="Allow manually uploading of certificates"
              />
            </FormControl>
          </div>
        </div>
      </Paper>
    );
  };

  const renderReviewSettings = () => (
    <Paper variant="outlined" square>
      <div className="pa3">
        <Typography variant="h6" className="pb2"><FormattedMessage id="trialSetup.reviewHeadline" /></Typography>
        <div className="pb3">
          <FormControl style={{ width: '100%' }} variant="outlined">
            <InputLabel><FormattedMessage id="trialSetup.reviewSelectionLabel" /></InputLabel>
            <Select
              label="Review Category"
              value={trial.reviewCategoryId || 1}
              style={{ width: '100%' }}
              onChange={(event) => onChangeValue('reviewCategoryId', Number(event.target.value))}
              name="review-category"
            >
              {
                Object.entries(reviewCategory).map(([key, value]) => (
                  <MenuItem
                    key={key}
                    value={key}
                    disabled={value.disable && trial.version !== 2}
                  >
                    {value.text}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </div>
      </div>
    </Paper>
  );

  const renderArchiveSettings = () => (
    <div className="mb3 b--dark-red ba">
      <Paper elevation={0} square>
        <div className="pa3">
          <Typography variant="h6" className="pb2"><FormattedMessage id="trialSetup.archiveHeadline" /></Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={trial.deactivate || false}
                onChange={(event) => onChangeValue('deactivate', event.target.checked)}
                color="primary"
              />
            )}
            label={<FormattedMessage id="trialSetup.deactivateTrial" />}
          />
        </div>
      </Paper>
    </div>
  );

  const renderSurveySettings = () => (
    <Paper variant="outlined" square>
      <Box sx={{ p: '1rem' }}>
        <Typography variant="h6" sx={{ pb: '0.5rem' }}><FormattedMessage id="trialSetup.surveyHeadline" /></Typography>
        <FormControl sx={{ width: '100%' }} variant="outlined">
          <InputLabel><FormattedMessage id="trialSetup.surveySelectionLabel" /></InputLabel>
          <Select
            label="Review Category"
            // only default to version 2 for newer trial
            // for older trials, it is version 1 even version is null
            value={trial.id && !trial.version ? 1 : trial.version || 2}
            sx={{ width: '100%' }}
            onChange={(event) => onChangeValue('version', Number(event.target.value))}
            name="survey-version"
          >
            {/* disable selection, as we are defaulting to newer version */}
            <MenuItem disabled key="1" value="1">1 (Alter Fragebogen-Editor)</MenuItem>
            <MenuItem disabled key="2" value="2">2 (Neuer Fragebogen-Editor)</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Paper>
  );

  return (
    <div className="pa2 mw8">
      <div className="pb3">
        {renderTrialSettings()}
      </div>
      <div className="pb3">
        {renderRegistrationSettings()}
      </div>
      <div className="pb3">
        {renderCertificateSettings()}
      </div>
      <div className="pb3">
        {renderSurveySettings()}
      </div>
      <div className="pb3">
        {renderReviewSettings()}
      </div>
      {
        trial.id && renderArchiveSettings()
      }
      <Box display="flex">
        <Box display="flex" gap={1} flexGrow={1}>
          <Button
            variant="contained"
            onClick={onCancel}
            color="secondary"
          >
            <FormattedMessage id="genericClose" />
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onSave(trial, selectedTags);
            }}
            disabled={!trial}
            color="primary"
          >
            <FormattedMessage id="save" />
          </Button>
        </Box>
        {
          onDuplicate && (
            <Button
              variant="contained"
              onClick={() => onDuplicate(trial)}
              disabled={!trial}
              color="primary"
            >
              <FormattedMessage id="duplicate" />
            </Button>
          )
        }
      </Box>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: state.users,
  trialCategories: state.trialCategories.categories,
  reviewCategoryMap: state.reviewCategory.reviewCategoryMap,
  trialTags: state.trialTag.byId,
});

const dispatchProps = {
  fetchTrialTags,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(TrialMetadataDialog);
